import {
  AfterViewInit,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';
import { filter, take, tap } from 'rxjs';
import { NomMassUpdateOperation } from 'src/app/core/constants';
import { injectPostUserCategoriesQuery } from 'src/app/api/queries/post.queries';
import { PostUserCategoriesEditorService } from 'src/app/core/services/post-user-categories-editor.service';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';
import { SearchableDialog, TabConfig } from 'src/app/shared/utils';
import { PostUserCategoriesListComponent } from '../post-user-categories/post-user-categories-list/post-user-categories-list.component';
import { CompanyCategory } from 'src/app/core/models/core.models';
import { faList, faTags } from '@fortawesome/free-solid-svg-icons';

interface DialogData extends TabDialogDataBase {
  postIds: string[];
  selectedPostsFeedId: string;
}

interface DialogResult {}

@Component({
  selector: 'app-post-user-categories-dialog',
  standalone: false,
  templateUrl: './post-user-categories-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './post-user-categories-dialog.component.scss',
})
export class PostUserCategoriesDialogComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);

  private readonly editor = inject(PostUserCategoriesEditorService);
  private readonly categoriesQuery = injectPostUserCategoriesQuery(
    computed(() => this.dialogData?.postIds ?? []),
    computed(() => this.dialogData?.selectedPostsFeedId),
  );

  // Seeds the working copy whenever a response lands. Only on data, so a re-open
  // keeps the previous tree on screen until the new one arrives.
  constructor() {
    super();
    effect(() => {
      const categories = this.categoriesQuery.data();

      if (categories) {
        this.editor.setTree(categories);
      }
    });
  }

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  objectActionIcon: IconDefinition | IconProp;
  objectIcon: IconDefinition | IconProp;

  objectActionTitle = '';
  objectTitle = '';
  objectTitleDefault = this.translateService.instant(
    'popup.object-title.untitled',
  );

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  readonly userCategories$ = toObservable(this.editor.categories);

  get hasActiveCategories(): boolean {
    return this.editor.hasActiveCategories();
  }

  NomMassUpdateOperation = NomMassUpdateOperation;
  updateOperationId: number = NomMassUpdateOperation.ADD;

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  get postIds() {
    return this.dialogData.postIds;
  }

  get selectedPostsFeedId() {
    return this.dialogData.selectedPostsFeedId;
  }

  ngOnInit(): void {
    this.setupUIState();
  }

  ngAfterViewInit(): void {
    this.setupTabs();
  }

  private setupUIState(): void {
    // apply translations and icons

    this.objectActionIcon = faTags;
    this.objectIcon = faList;

    this.objectActionTitle = this.translateService.instant(
      'post-user-categories.title.user-content-categories-list',
    );
    this.objectTitle = this.translateService.instant(
      'post-user-categories.title.user-content-categories',
    );
  }

  private setupTabs(): void {
    this.userCategories$
      .pipe(
        filter((userCategories): userCategories is CompanyCategory[] =>
          Array.isArray(userCategories),
        ),
        take(1),
        tap((userCategories) => {
          this.tabs = userCategories.map(
            (companyCategory: CompanyCategory) => ({
              id: companyCategory.company_category_name,
              label: companyCategory.company_category_name,
              icon: ['fas', 'tag'],
              component: PostUserCategoriesListComponent,
              inputs: {
                companyCategoryId: companyCategory.company_category_id,
              },
              count: companyCategory.count_selected,
            }),
          );

          // Assuming you want to select the first tab only once as well
          if (this.tabs.length > 0) {
            this.initializeBase();
            this.tabSearch.setupAutoSwitch(this.tabs);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  clearSelected() {
    this.editor.clearSelected();
  }

  protected override performSave(): DialogCloseOptions<DialogResult> {
    return {
      reason: DialogCloseReason.Save,
      data: {
        categories: this.editor.groupedSelectedCategories(),
        updateOperationId: this.updateOperationId,
      },
    };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
